<template>
  <PbsActionButton
    v-if="props.detail"
    :label="$t('general.moreActions')"
    :items="actionButtonItems"
    :mod-vertical="modVertical"
    :mod-large="modLarge"
  />
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useI18n } from 'vue-i18n';
import EntityModelStatusForm from '../entity-model-status-form/EntityModelStatusForm.vue';
import EntityModelDuplicationForm from '../entity-model-duplication-form/EntityModelDuplicationForm.vue';
import EntityModelCascadeDeleteForm from '../entity-model-cascade-delete-form/EntityModelCascadeDeleteForm.vue';
import PbsActionButton, { PbsActionButtonItem } from '@/components/PbsActionButton.vue';
import { EntityModelDetailDto, SearchEntityModelDto } from '@/api/backoffice/clients';
import { EntityModelActionButtonType } from '@/modules/configuration/common';
import { useEntityModelDetailStore } from '@/modules/configuration/store/entity-model/entity-model-detail.store';

const store = useEntityModelDetailStore();
const modalStore = useModalStore();
const { t } = useI18n();

type EntityModelActionButtonProps = {
  detail: EntityModelDetailDto | SearchEntityModelDto;
  modVertical?: boolean;
  modLarge?: boolean;
  actions: EntityModelActionButtonType[];
};

const props = withDefaults(defineProps<EntityModelActionButtonProps>(), {
  modVertical: false,
  modLarge: false,
  detail: null,
  actions: () => []
});

const actionButtonItems = computed((): PbsActionButtonItem[] => {
  const items = createActionButtonItems();
  return props.actions?.map((action) => items.find((x) => x.name === action)).filter((x) => !!x) ?? [];
});

const createActionButtonItems = () => {
  const isDraft = props.detail?.statusId === 0;

  return [
    {
      name: EntityModelActionButtonType.ChangeStatus,
      label: t('entitymodel.actions.changeStatus.label'),
      click: showChangeStatusModal,
      ellipsis: true
    },
    {
      name: EntityModelActionButtonType.Duplicate,
      label: t('entitymodel.actions.duplicate.label'),
      click: showDuplicationModal,
      ellipsis: true
    },
    {
      name: EntityModelActionButtonType.CascadeDelete,
      label: t('entitymodel.actions.cascadeDelete.label'),
      click: showCascadeDeleteModal,
      disabled: !isDraft,
      ellipsis: true
    },
    {
      name: EntityModelActionButtonType.Export,
      label: t('general.export'),
      click: doExport
    }
  ];
};

const showChangeStatusModal = () => {
  const modalContent = {
    title: t('entitymodel.actions.changeStatus.description'),
    component: EntityModelStatusForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showDuplicationModal = () => {
  const modalContent = {
    title: t('entitymodel.actions.duplicate.description'),
    component: EntityModelDuplicationForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const showCascadeDeleteModal = () => {
  const modalContent = {
    closable: false,
    title: t('entitymodel.actions.cascadeDelete.description'),
    component: EntityModelCascadeDeleteForm,
    props: {
      id: props.detail.id
    }
  };

  modalStore.showModal(modalContent);
};

const doExport = async () => {
  const id = props.detail.id;
  await store.export(id);
};
</script>

<style lang="scss" scoped></style>
